import React, {
  ComponentProps,
  ComponentType,
  FunctionComponent,
  PropsWithChildren,
} from 'react';
import { FeaturesReady, IfFeatureEnabled } from '@growthbook/growthbook-react';
import {
  FeatureFlagAdapter,
  featureFlagAdapter,
  FeatureFlagBoolKeys,
} from '@monorepo/feature-flag/shared';
import { useBooleanFeatureFlag } from './feature-flag.hooks';

type WithFeatureFlagProps = PropsWithChildren & FeatureFlagAdapter;

/**
 * HOC to conditionally render children based on a feature flag.
 * @param children - The children to render if the feature flag is enabled.
 * @param featureFlag - FeatureFlagAdapter
 */
export const WithFeatureFlag: FunctionComponent<WithFeatureFlagProps> = ({
  children,
  ...featureFlag
}) => {
  return (
    <IfFeatureEnabled feature={featureFlagAdapter(featureFlag)}>
      {children}
    </IfFeatureEnabled>
  );
};

type WithWaitForFeatureProps = PropsWithChildren &
  ComponentProps<typeof FeaturesReady>;

/**
 * HOC to wait for all feature flag to be ready before rendering children.
 * @param children - The children to render once all feature flags are ready.
 * @param timeout - Optional timeout in milliseconds to wait for feature flags.
 * @param fallback - Optional fallback component to render if the timeout is reached.
 */
export const WithWaitForFeature: FunctionComponent<WithWaitForFeatureProps> = ({
  children,
  ...props
}) => {
  return <FeaturesReady {...props}>{children}</FeaturesReady>;
};

/**
 * Higher-Order Component that conditionally renders one of two components based on a boolean feature flag.
 *
 * This HOC creates a wrapper component that evaluates the specified feature flag and renders
 * either the Nova component (when enabled) or the Original component (when disabled).
 * All props are forwarded to the selected component without modification.
 *
 * @template P - The props type that both components must accept
 * @param featureFlag - The feature flag key to evaluate for the conditional rendering - must start with "enable-"
 * @param OriginalComponent - The component to render when the feature flag is disabled/false
 * @param NovaComponent - The component to render when the feature flag is enabled/true
 * @param adapter - Optional adapter configuration (env will be merged with EnvService if provided)
 * @returns A wrapper component that conditionally renders based on the feature flag state
 *
 * @example
 * ```tsx
 * const ConditionalLogin = withBoolFeatureGate(
 *   'enable-nova-auth-pages',
 *   Login,
 *   LoginNova
 * );
 * ```
 *
 * @example
 * ```tsx
 * const ConditionalLogin = withBoolFeatureGate(
 *   'enable-nova-auth-pages',
 *   Login,
 *   LoginNova,
 *   { env: Application.THE5ERS }
 * );
 * ```
 */
export const withBoolFeatureGate = <P extends object>(
  featureFlag: FeatureFlagBoolKeys,
  OriginalComponent: ComponentType<P>,
  NovaComponent: ComponentType<P>,
  adapter: Omit<FeatureFlagAdapter, 'flag'>,
) => {
  return (props: P) => {
    const isEnabled = useBooleanFeatureFlag(featureFlag, adapter || {});
    const Component = isEnabled ? NovaComponent : OriginalComponent;
    return <Component {...props} />;
  };
};
